import { type MutableRefObject, useEffect, useRef } from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/**
 * Loads checkpoint shapes into ShapeManager once per checkpoint (`checkpointLoadKey`), as soon as
 * both the manager and the shapes are available.
 */
export function useCheckpointLoad({
  internalShapeManagerRef,
  checkpointShapes,
  checkpointLoadKey,
  onCheckpointTextBoxesApplied,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  checkpointShapes: CenterCanvasConfig['checkpointShapes']
  checkpointLoadKey: CenterCanvasConfig['checkpointLoadKey']
  onCheckpointTextBoxesApplied: CenterCanvasConfig['onCheckpointTextBoxesApplied']
}) {
  const lastCheckpointLoadKeyRef = useRef<string | undefined>(checkpointLoadKey)
  const didInitialCheckpointLoadRef = useRef(false)

  // Load checkpoint shapes into ShapeManager (single-shot, static-load optimized).
  //
  // `checkpointShapes` is the ONE array — text frames included. A `paragraphs`-bearing ShapeData
  // already renders (via ShapeRenderer's shape-text branch) and edits (via `enterShapeTextEdit`,
  // routed by `isShapeTextFrame` in `use-shape-double-click.ts`) through ShapeManager's
  // existing, generic-over-ShapeData mechanics — selection, drag, resize, rotate, delete, lock,
  // freeze, undo, and multiplayer sync all already work for any shape carrying `paragraphs`.
  // This deliberately defers D1's contentEditable-for-simple-rectangles renderer
  // (`docs/text-unification.md`) — every loaded text frame uses the scanline shape-text engine
  // for now, not TextRenderer.
  //
  // Do NOT reintroduce a second "wait for the text array too" condition here: Phase 3 deleted
  // `checkpointToText`, so a `checkpointTextBoxes` prop is never passed. Guarding on it made this
  // effect return early every time and NOTHING rendered at all, app-wide, on every load.
  useEffect(() => {
    // If the caller indicates a new checkpoint/spread, allow one more initial load.
    if (lastCheckpointLoadKeyRef.current !== checkpointLoadKey) {
      lastCheckpointLoadKeyRef.current = checkpointLoadKey
      didInitialCheckpointLoadRef.current = false
    }

    if (!internalShapeManagerRef.current) return
    if (!checkpointShapes) return
    if (didInitialCheckpointLoadRef.current) return

    internalShapeManagerRef.current.loadShapes(checkpointShapes, {
      clearExisting: true,
    })
    didInitialCheckpointLoadRef.current = true
    onCheckpointTextBoxesApplied?.()
  }, [checkpointShapes, checkpointLoadKey, onCheckpointTextBoxesApplied, internalShapeManagerRef])
}
